/**
 * BudgetConfigPanel Component
 *
 * Allows users to configure their usage budget settings:
 * - Set monthly spending limit
 * - Configure alert thresholds (default: 50%, 75%, 90%)
 * - Enable/disable email alerts
 * - View current budget status
 *
 * Technical Design Reference: docs/technical/TD-AC-594-token-audit.md
 * Related Tickets:
 * - AC-594 (Epic: Token Audit System)
 * - AC-611 (Frontend: BudgetConfigPanel component)
 * - AC-612 (Parent: UsageDashboard container)
 */

import React, { useState, useEffect } from 'react';
import { Settings, Trash2, AlertCircle, Loader2 } from 'lucide-react';
import type { UserBudget, BudgetConfig } from '../../types/tokenUsage';

/**
 * Available alert threshold options.
 */
const ALERT_THRESHOLD_OPTIONS = [50, 75, 90] as const;

export interface BudgetConfigPanelProps {
  /** Current user budget configuration (null if not set) */
  budget: UserBudget | null;
  /** Whether data is currently loading */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Callback to save budget configuration */
  onSave: (config: BudgetConfig) => Promise<void>;
  /** Callback to delete budget configuration */
  onDelete: () => Promise<void>;
}

/**
 * Loading skeleton for the budget config panel.
 */
const LoadingSkeleton: React.FC = () => (
  <div
    role="status"
    aria-busy="true"
    aria-label="Loading budget configuration"
    data-testid="budget-config-skeleton"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 animate-pulse"
  >
    <div className="flex items-center mb-6">
      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded" />
      <div className="ml-3 h-6 bg-gray-200 dark:bg-gray-700 rounded w-48" />
    </div>
    <div className="space-y-4">
      <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
      <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
      <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
    </div>
  </div>
);

/**
 * Error state showing error message with icon.
 */
const ErrorState: React.FC<{ message: string }> = ({ message }) => (
  <div
    role="alert"
    className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 flex items-center"
  >
    <AlertCircle
      data-testid="budget-error-icon"
      className="w-6 h-6 text-red-500 mr-3 flex-shrink-0"
    />
    <span className="text-red-700 dark:text-red-400">{message}</span>
  </div>
);

/**
 * Confirmation dialog for delete action.
 */
const DeleteConfirmDialog: React.FC<{
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}> = ({ isOpen, onConfirm, onCancel, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div
      data-testid="delete-confirm-dialog"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-cap-dark rounded-lg shadow-xl p-6 max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-2">
          Delete Budget Configuration
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Are you sure you want to delete your budget configuration? This will disable all spending limits and alerts.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="confirm-delete-button"
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center"
          >
            {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Format currency for display.
 * Uses adaptive precision: 4 decimal places for amounts under $1,
 * 2 decimal places for larger amounts.
 */
const formatCurrency = (value: number): string => {
  const decimalPlaces = value < 1 && value > 0 ? 4 : 2;
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}`;
};

/**
 * BudgetConfigPanel component for managing budget settings.
 */
const BudgetConfigPanel: React.FC<BudgetConfigPanelProps> = ({
  budget,
  loading = false,
  error = null,
  onSave,
  onDelete,
}) => {
  // Form state
  const [monthlyLimit, setMonthlyLimit] = useState<string>('');
  const [alertThresholds, setAlertThresholds] = useState<number[]>([50, 75, 90]);
  const [emailAlertsEnabled, setEmailAlertsEnabled] = useState<boolean>(false);

  // UI state
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  // Initialize form with budget data
  useEffect(() => {
    if (budget) {
      setMonthlyLimit(budget.monthly_limit.toString());
      setAlertThresholds(budget.alert_thresholds);
      setEmailAlertsEnabled(budget.email_alerts_enabled);
    } else {
      // Reset to defaults when no budget
      setMonthlyLimit('');
      setAlertThresholds([50, 75, 90]);
      setEmailAlertsEnabled(false);
    }
  }, [budget]);

  /**
   * Validate monthly limit input.
   */
  const validateMonthlyLimit = (value: string): boolean => {
    const numValue = parseFloat(value);
    if (!value || isNaN(numValue)) {
      setValidationError('Monthly limit is required');
      return false;
    }
    if (numValue <= 0) {
      setValidationError('Monthly limit must be greater than $0');
      return false;
    }
    setValidationError(null);
    return true;
  };

  /**
   * Handle threshold checkbox toggle.
   */
  const handleThresholdToggle = (threshold: number) => {
    setAlertThresholds((prev) =>
      prev.includes(threshold)
        ? prev.filter((t) => t !== threshold)
        : [...prev, threshold].sort((a, b) => a - b)
    );
  };

  /**
   * Handle form submission.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateMonthlyLimit(monthlyLimit)) {
      return;
    }

    const config: BudgetConfig = {
      monthly_limit: parseFloat(monthlyLimit),
      alert_thresholds: alertThresholds,
      email_alerts_enabled: emailAlertsEnabled,
    };

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      await onSave(config);
      setSaveSuccess(true);
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      // Error is handled by parent component
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle delete confirmation.
   */
  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteConfirm(false);
    } catch (err) {
      // Error is handled by parent component
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle monthly limit input change.
   */
  const handleMonthlyLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMonthlyLimit(value);
    if (validationError) {
      validateMonthlyLimit(value);
    }
  };

  // Show loading state
  if (loading) {
    return <LoadingSkeleton />;
  }

  // Show error state
  if (error) {
    return <ErrorState message={error} />;
  }

  const hasBudget = budget !== null;

  return (
    <div
      data-testid="budget-config-panel"
      className="bg-white dark:bg-cap-dark rounded-lg shadow p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Settings
            data-testid="budget-settings-icon"
            className="w-6 h-6 text-gray-600 dark:text-gray-400 mr-3"
          />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">
            {hasBudget ? 'Budget Configuration' : 'Set Up Budget'}
          </h2>
        </div>
        {hasBudget && (
          <button
            type="button"
            data-testid="delete-budget-button"
            onClick={() => setShowDeleteConfirm(true)}
            className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            aria-label="Delete budget"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Current Budget Status (when budget exists) */}
      {hasBudget && (
        <div
          data-testid="current-budget-status"
          className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6"
        >
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Budget</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Monthly Limit:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">
                {formatCurrency(budget.monthly_limit)}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Email Alerts:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">
                {budget.email_alerts_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="col-span-2">
              <span className="text-gray-500 dark:text-gray-400">Alert Thresholds:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">
                {budget.alert_thresholds.map((t) => `${t}%`).join(', ')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Budget Configuration Form */}
      <form onSubmit={handleSubmit} data-testid="budget-config-form">
        {/* Monthly Limit Input */}
        <div className="mb-4">
          <label
            htmlFor="monthly-limit"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Monthly Spending Limit
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
              $
            </span>
            <input
              id="monthly-limit"
              type="number"
              data-testid="monthly-limit-input"
              value={monthlyLimit}
              onChange={handleMonthlyLimitChange}
              onBlur={() => validateMonthlyLimit(monthlyLimit)}
              min="0.01"
              step="0.01"
              placeholder="100.00"
              required
              aria-invalid={validationError ? 'true' : 'false'}
              aria-describedby={validationError ? 'monthly-limit-error' : undefined}
              className={`w-full pl-7 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-warm-white ${
                validationError
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            />
          </div>
          {validationError && (
            <p
              id="monthly-limit-error"
              data-testid="validation-error"
              className="mt-1 text-sm text-red-600"
              role="alert"
            >
              {validationError}
            </p>
          )}
        </div>

        {/* Alert Thresholds */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Alert Thresholds
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Receive alerts when spending reaches these percentages of your monthly limit.
          </p>
          <div
            data-testid="alert-thresholds"
            className="flex flex-wrap gap-3"
            role="group"
            aria-label="Alert threshold options"
          >
            {ALERT_THRESHOLD_OPTIONS.map((threshold) => (
              <label
                key={threshold}
                className={`flex items-center px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                  alertThresholds.includes(threshold)
                    ? 'bg-primary-50 dark:bg-primary-900/30 border-blue-500 dark:border-blue-400 text-blue-700 dark:text-blue-300'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <input
                  type="checkbox"
                  data-testid={`threshold-${threshold}`}
                  checked={alertThresholds.includes(threshold)}
                  onChange={() => handleThresholdToggle(threshold)}
                  className="sr-only"
                />
                <span>{threshold}%</span>
              </label>
            ))}
          </div>
        </div>

        {/* Email Alerts Toggle */}
        <div className="mb-6">
          <label className="flex items-center cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                data-testid="email-alerts-toggle"
                checked={emailAlertsEnabled}
                onChange={(e) => setEmailAlertsEnabled(e.target.checked)}
                className="sr-only"
              />
              <div
                className={`w-10 h-6 rounded-full transition-colors ${
                  emailAlertsEnabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <div
                  className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform absolute top-1 ${
                    emailAlertsEnabled ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </div>
            </div>
            <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              Enable email alerts
            </span>
          </label>
          <p className="mt-1 ml-13 text-xs text-gray-500 dark:text-gray-400">
            Receive email notifications when spending thresholds are reached.
          </p>
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <div
            data-testid="save-success-message"
            className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm"
            role="status"
          >
            Budget configuration saved successfully.
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          data-testid="save-budget-button"
          disabled={isSaving}
          className="w-full py-2 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {isSaving ? 'Saving...' : hasBudget ? 'Update Budget' : 'Set Budget'}
        </button>
      </form>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={showDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default BudgetConfigPanel;
